import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { invokeEdgeFunction } from '@/lib/edgeFunctionClient';
import { useToast } from '@/hooks/use-toast';

export interface TraditionalAchApproval {
  id: string;
  payment_transaction_id: string | null;
  job_id: string;
  purchase_order_id: string | null;
  client_organization_id: string;
  amount_cents: number;
  reference_id: string;
  status: 'pending' | 'approved' | 'rejected';
  admin_notes: string | null;
  approved_by_user_id: string | null;
  approved_at: string | null;
  rejected_by_user_id: string | null;
  rejected_at: string | null;
  rejection_reason: string | null;
  created_at: string;
  updated_at: string;
  // Joined data
  jobs?: {
    id: string;
    title: string;
    public_id: string;
  };
  organizations?: {
    id: string;
    name: string;
  };
}

/**
 * Hook to fetch traditional ACH approvals by status
 */
export function useTraditionalAchApprovals(status?: 'pending' | 'approved' | 'rejected') {
  return useQuery({
    queryKey: ['traditional-ach-approvals', status],
    queryFn: async () => {
      let query = supabase
        .from('traditional_ach_approvals')
        .select(`
          *,
          jobs:job_id (id, title, public_id),
          organizations:client_organization_id (id, name)
        `)
        .order('created_at', { ascending: false });

      if (status) {
        query = query.eq('status', status);
      }

      const { data, error } = await query;

      if (error) throw error;
      return data as unknown as TraditionalAchApproval[];
    },
  });
}

/**
 * Hook to approve a traditional ACH payment
 */
export function useApproveTraditionalAch() {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async ({ approvalId, adminNotes }: { approvalId: string; adminNotes: string }) => {
      const { data, error } = await invokeEdgeFunction('approve-traditional-ach', {
        body: { approvalId, adminNotes },
        callerOperation: 'useApproveTraditionalAch.mutate',
      });

      if (error) throw error;
      if ((data as { error?: string })?.error) throw new Error((data as { error: string }).error);
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['traditional-ach-approvals'] });
      toast({
        title: 'Payment Approved',
        description: 'Traditional ACH payment has been marked as received.',
      });
    },
    onError: (error: Error) => {
      toast({
        title: 'Approval Failed',
        description: error.message,
        variant: 'destructive',
      });
    },
  });
}

/**
 * Hook to reject a traditional ACH payment
 */
export function useRejectTraditionalAch() {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async ({ approvalId, rejectionReason }: { approvalId: string; rejectionReason: string }) => {
      const { data, error } = await invokeEdgeFunction('reject-traditional-ach', {
        body: { approvalId, rejectionReason },
        callerOperation: 'useRejectTraditionalAch.mutate',
      });

      if (error) throw error;
      if ((data as { error?: string })?.error) throw new Error((data as { error: string }).error);
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['traditional-ach-approvals'] });
      toast({
        title: 'Payment Rejected',
        description: 'Traditional ACH payment has been rejected.',
      });
    },
    onError: (error: Error) => {
      toast({
        title: 'Rejection Failed',
        description: error.message,
        variant: 'destructive',
      });
    },
  });
}
